<template>
  <warning-banner
    v-if="reportError"
    description="Could not load all reports for this tag. Refresh the dashboard if reports were removed or access changed."
  >
    <template #action>
      <q-btn
        flat
        no-caps
        label="Retry tag reports"
        :loading="reportLoading"
        @click="retryReports"
      />
    </template>
  </warning-banner>
  <q-inner-loading v-else-if="reportLoading || organizationLoading" showing color="secondary" />
  <property-compare
    v-else
    :comparable-addresses="synetheticReport.comparable_addresses"
    :subject="synetheticReport.subject"
    :report="synetheticReport"
  />
</template>

<script lang="ts">
import PropertyCompare from '@/components/PropertyReports/PropertyCompare.vue'
import WarningBanner from '@/components/WarningBanner.vue'
import type { PropertyEdits, PropertyReportSubscriptionAttributes } from 'common'
import { usePropertyReportStore } from '@/stores/propertyreport'
import { useRoute } from 'vue-router'
import { useUserStore } from '@/stores/user'

export default {
  name: 'PropertyReportByTagView',
  components: {
    WarningBanner,
    PropertyCompare
  },
  data() {
    return {
      reportLoading: false,
      reportError: false,
      requestGeneration: 0,
      reports: [] as PropertyReportSubscriptionAttributes[]
    }
  },
  computed: {
    synetheticReport() {
      const subjectIds = [
        ...new Set(this.reports.flatMap((_) => (_.subject ? [_.subject.id] : [])))
      ]
      const comparableAddresses = subjectIds.map(
        (subjectId) => this.reports.find((_) => _.subject?.id === subjectId)!.subject
      )
      const propertyEdits: PropertyEdits[] = []
      for (const report of this.reports) {
        if (report.property_edits?.length) {
          propertyEdits.push(...report.property_edits)
        }
      }
      return {
        id: -1,
        name: `${useRoute().query['tag'] as string} Report`,
        subject: null,
        comparable_addresses: comparableAddresses,
        excluded_property_ids: [],
        user_id: '',
        modified_on: this.reports.length
          ? this.reports
              .map((_) => _.modified_on)
              .reduce((a, b) => (a > b ? a : b), this.reports[0]!.modified_on)
          : new Date(),
        created_on: this.reports.length
          ? this.reports
              .map((_) => _.created_on)
              .reduce((a, b) => (a > b ? a : b), this.reports[0]!.created_on)
          : new Date(),
        deleted_on: null,
        public_id: null,
        is_organization_visible: false, // TODO: Delete me
        organization_visibility: null,
        allow_nonowner_development_edits: false,
        tags: [this.tag],
        feature_flags: [],
        property_edits: propertyEdits,
        view_preferences: []
      } as PropertyReportSubscriptionAttributes
    },
    tag() {
      const route = useRoute()
      return route.query['tag'] as string
    },
    organizationLoading() {
      return useUserStore().organizationLoading
    }
  },
  watch: {
    tag() {
      this.loadReports()
    }
  },
  created() {
    this.loadReports()
  },
  methods: {
    retryReports() {
      this.$tracker.retriedDashboardReports('tag_report')
      void this.loadReports()
    },
    async loadReports() {
      const generation = ++this.requestGeneration
      if (this.tag) {
        this.reportLoading = true
        this.reportError = false
        await usePropertyReportStore()
          .getByTag(this.tag)
          .then((response) => {
            if (generation !== this.requestGeneration) {
              return
            }
            if (!response.length) {
              this.$router.push({ name: 'Dashboard' })
            } else {
              this.reports = response
            }
          })
          .catch(() => {
            if (generation === this.requestGeneration) {
              this.reportError = true
            }
          })
          .finally(() => {
            if (generation === this.requestGeneration) {
              this.reportLoading = false
            }
          })
      }
    }
  }
}
</script>
